/* The shape every row-bearing store holds, and the only place a delta is
 * applied to it.
 *
 * Two properties matter and both are structural rather than cosmetic:
 *
 *   A load phase never blanks data. `phase` moves independently of `byId`, so a
 *   refresh, a filter change and a live delta all keep the current rows on
 *   screen. A table renders its skeleton only for a first load, which is
 *   `isFirstLoad()` below and nothing else.
 *
 *   A delta touches only the documents that changed. Every other entry in
 *   `byId` keeps its object identity, so React reconciles those rows in place
 *   instead of re-creating their subtrees. This is why the live path patches
 *   rather than replaces. */

/** `unconfigured` is NOT a synonym for empty. It means no source was ever
 *  wired — which is a different fact from a source that ran and matched
 *  nothing, and an operator acts differently on each. */
export type Phase = 'unconfigured' | 'loading' | 'ready' | 'error';

export interface Collection<T> {
  byId: Record<string, T>;
  /** Display order. Replaced atomically on a reset; spliced on an add. */
  ids: string[];
  phase: Phase;
  error?: string;
  /** The query generation that owns the current contents. A callback carrying a
   *  stale generation is dropped — see `applyEvent`. */
  gen: number;
  /** When the contents last changed, for "updated Xs ago" affordances. */
  updatedAt: number;
  /** The source's cursor for the next page, when it said there is one. */
  nextCursor?: string;
}

export interface Doc<T> {
  id: string;
  doc: T;
}

export type DocChange<T> =
  | { kind: 'added' | 'modified'; id: string; doc: T }
  | { kind: 'removed'; id: string };

/** A delta stream alone cannot express "this query matched nothing", so a
 *  source emits the initial result as its own event. */
export type WatchEvent<T> =
  /** The initial result. `nextCursor` is the source's own opaque cursor for the
   *  page after this one, absent on the last page. The console never
   *  constructs one — see spec/console.md. */
  | { type: 'reset'; docs: Doc<T>[]; nextCursor?: string }
  /** Subsequent deltas. `nextCursor` is the page boundary RECOMPUTED for this
   *  snapshot: the window is bounded and moves as rows arrive, so a cursor
   *  captured once at load goes stale. Absent means "unchanged", not "gone" —
   *  a source that cannot recompute one must not blank the existing cursor and
   *  disable paging. */
  | { type: 'change'; changes: DocChange<T>[]; nextCursor?: string }
  | { type: 'error'; message: string };

export type Unsubscribe = () => void;

export function emptyCollection<T>(): Collection<T> {
  return { byId: {}, ids: [], phase: 'unconfigured', gen: 0, updatedAt: 0 };
}

/** True only for a genuine first load. Anything else keeps its rows. */
export function isFirstLoad(c: Collection<unknown>): boolean {
  return c.phase === 'loading' && c.ids.length === 0;
}

/** No source was ever wired — distinct from a source that returned nothing. */
export function isUnconfigured(c: Collection<unknown>): boolean {
  return c.phase === 'unconfigured';
}

/** Ran, succeeded, matched nothing. */
export function isEmptyResult(c: Collection<unknown>): boolean {
  return c.phase === 'ready' && c.ids.length === 0;
}

/** Open a new query generation. Rows stay on screen; only the phase moves. */
export function beginQuery<T>(c: Collection<T>): Collection<T> {
  return { ...c, phase: 'loading', error: undefined, gen: c.gen + 1 };
}

export type Comparator<T> = (a: Doc<T>, b: Doc<T>) => number;

/** Apply one source event.
 *
 * `gen` is the generation the caller subscribed under. A callback from a watch
 * the store has already replaced carries an older generation and is dropped:
 * without that, a slow response to a filter the operator has moved off would
 * overwrite the filter they are now looking at.
 *
 * Returns the SAME collection object when nothing applies, so a no-op event
 * cannot cause a render. */
export function applyEvent<T>(
  c: Collection<T>,
  gen: number,
  ev: WatchEvent<T>,
  compare?: Comparator<T>,
): Collection<T> {
  if (gen !== c.gen) return c;

  if (ev.type === 'error') {
    // The rows already on screen are still the truth as of the last successful
    // read, so they stay. Only the phase says the stream is broken.
    return { ...c, phase: 'error', error: ev.message };
  }

  if (ev.type === 'reset') {
    const byId: Record<string, T> = {};
    const docs = compare ? [...ev.docs].sort(compare) : ev.docs;
    for (const d of docs) byId[d.id] = d.doc;
    return {
      byId,
      ids: docs.map((d) => d.id),
      phase: 'ready',
      gen,
      updatedAt: Date.now(),
      nextCursor: ev.nextCursor,
    };
  }

  if (ev.changes.length === 0) return c;

  // Copy the container, not the entries: untouched documents keep identity.
  const byId = { ...c.byId };
  let ids = c.ids;
  let idsDirty = false;

  for (const ch of ev.changes) {
    if (ch.kind === 'removed') {
      if (!(ch.id in byId)) continue;
      delete byId[ch.id];
      if (!idsDirty) {
        ids = [...ids];
        idsDirty = true;
      }
      const at = ids.indexOf(ch.id);
      if (at >= 0) ids.splice(at, 1);
      continue;
    }

    const isNew = !(ch.id in byId);
    byId[ch.id] = ch.doc;

    // A new row has to be placed. An EXISTING row has to be RE-placed if the
    // change moved it under the comparator — a client key renamed A -> Z that
    // stays sitting before B is a sorted table quietly telling a lie.
    if (!isNew && !compare) continue;
    if (!isNew && compare && stillOrdered(ids, byId, ch.id, compare)) continue;

    if (!idsDirty) {
      ids = [...ids];
      idsDirty = true;
    }
    if (!isNew) {
      const from = ids.indexOf(ch.id);
      if (from >= 0) ids.splice(from, 1);
    }
    if (!compare) {
      ids.push(ch.id);
      continue;
    }
    ids.splice(placeFor(ids, byId, ch.id, ch.doc, compare), 0, ch.id);
  }

  return {
    byId,
    ids,
    phase: 'ready',
    gen,
    updatedAt: Date.now(),
    // Absent means unchanged. Dropping it here would disable "Next" the moment
    // the first live row arrived.
    nextCursor: ev.nextCursor ?? c.nextCursor,
    ...(c.error ? { error: undefined } : {}),
  };
}

/** Where `id` belongs in `ids` under `compare`. `ids` must not contain it. */
function placeFor<T>(
  ids: string[],
  byId: Record<string, T>,
  id: string,
  doc: T,
  compare: Comparator<T>,
): number {
  const entry: Doc<T> = { id, doc };
  for (let i = 0; i < ids.length; i++) {
    const other = byId[ids[i]!];
    if (other !== undefined && compare(entry, { id: ids[i]!, doc: other }) < 0) return i;
  }
  return ids.length;
}

/** True when `id` is already in a position the comparator agrees with, so a
 *  modification that did not touch the sort field costs no array work. */
function stillOrdered<T>(
  ids: string[],
  byId: Record<string, T>,
  id: string,
  compare: Comparator<T>,
): boolean {
  const at = ids.indexOf(id);
  if (at < 0) return false;
  const me: Doc<T> = { id, doc: byId[id]! };
  const before = at > 0 ? ids[at - 1]! : undefined;
  const after = at < ids.length - 1 ? ids[at + 1]! : undefined;
  if (before !== undefined && compare({ id: before, doc: byId[before]! }, me) > 0) return false;
  if (after !== undefined && compare(me, { id: after, doc: byId[after]! }) > 0) return false;
  return true;
}

/** Materialise the rows in display order. Stable while nothing changed, because
 *  both `ids` and every untouched entry keep their identity. */
export function rowsOf<T>(c: Collection<T>): T[] {
  const out: T[] = [];
  for (const id of c.ids) {
    const doc = c.byId[id];
    if (doc !== undefined) out.push(doc);
  }
  return out;
}

/** Seed a collection outright. Only the fixture harness uses this; a live store
 *  always arrives through `applyEvent`. */
export function seeded<T>(docs: Doc<T>[], compare?: Comparator<T>): Collection<T> {
  return applyEvent(
    { ...emptyCollection<T>(), gen: 1 },
    1,
    { type: 'reset', docs },
    compare,
  );
}
